import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import fetchAllCentersWithIds from "../List/CenterList";
import CustomDatePicker from "../CustomDatePicker";
import api from "../../config/URL";
import { toast } from "react-toastify";
import { BsEraser } from "react-icons/bs";
import { format } from "date-fns";
import { Link } from "react-router-dom";

function RevenueReports({ selectedCenter }) {
  const [centerData, setCenterData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);

  const [filters, setFilters] = useState({
    centerId: "",
    courseType: "",
    month: "",
    fromDate: "",
    toDate: "",
    paymentMethod: "",
  });

  const fetchRevenueReportsData = async () => {
    if (!filters.centerId) return;

    try {
      const params = new URLSearchParams();
      params.append("centerId", filters.centerId);

      if (filters.courseType) params.append("courseType", filters.courseType);
      if (filters.month) params.append("month", filters.month);
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.paymentMethod)
        params.append("paymentMethod", filters.paymentMethod);

      const response = await api.get(
        `globalRevenueReport?${params.toString()}`
      );
      setRevenueData(response.data);
    } catch (error) {
      toast.error(error.message || "Error fetching data");
    }
  };

  const fetchCenterData = async () => {
    try {
      const centers = await fetchAllCentersWithIds();
      setCenterData(centers);

      const defaultCenterId =
        selectedCenter || (centers.length > 0 ? centers[0].id : "");
      setFilters((prevFilters) => ({
        ...prevFilters,
        centerId: defaultCenterId,
      }));
    } catch (error) {
      toast.error(error.message || "Error fetching centers");
    }
  };

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({ ...prev, [type]: value }));
  };

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      courseType: "",
      month: "",
      fromDate: "",
      toDate: "",
      paymentMethod: "",
    }));
  };

  useEffect(() => {
    fetchCenterData();
  }, []);

  useEffect(() => {
    fetchRevenueReportsData();
  }, [filters]);

  const chartData = {
    series: [
      {
        name: "Actual",
        data: revenueData ? revenueData.map((item) => item.actual) : [],
      },
      {
        name: "Projection",
        data: revenueData ? revenueData.map((item) => item.projection) : [],
      },
    ],
    options: {
      chart: { type: "bar", height: 350 },
      plotOptions: { bar: { horizontal: false, columnWidth: "55%" } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      xaxis: {
        categories: revenueData
          ? revenueData.map(
              (item) =>
                item.month.charAt(0).toUpperCase() +
                item.month.slice(1).toLowerCase()
            )
          : [],
      },
      yaxis: {
        title: { text: "(thousands)" },
        labels: {
          formatter: (val) => val.toLocaleString(),
        },
      },
      fill: {
        opacity: 1,
        colors: ["#0000FF", "#FF0000"],
      },
      tooltip: { y: { formatter: (val) => `${val.toLocaleString()}` } },
      title: {
        text: `Revenue Report`,
        align: "center",
        style: { fontSize: "16px", fontWeight: "bold" },
      },
    },
  };

  return (
    <div className="p-1">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Report Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Revenue Report
        </li>
      </ol>
      <div
        className="d-flex justify-content-between align-items-center my-2"
        style={{ gap: "10px" }}
      >
        <select
          className="form-select form-select-sm w-30"
          name="centerId"
          style={{ width: "100%" }}
          onChange={(e) => handleFilterChange("centerId", e.target.value)}
          value={filters.centerId}
        >
          <option value="" disabled>
            Select a Center
          </option>
          {centerData?.map((center) => (
            <option key={center.id} value={center.id}>
              {center.centerNames}
            </option>
          ))}
        </select>

        <select
          className="form-select form-select-sm w-30"
          onChange={(e) => handleFilterChange("courseType", e.target.value)}
          value={filters.courseType}
        >
          <option value="" disabled>
            Course Type
          </option>
          <option value="Normal">Normal</option>
          <option value="Holiday">Holiday</option>
        </select>

        <select
          className="form-select form-select-sm w-50"
          onChange={(e) => handleFilterChange("paymentMethod", e.target.value)}
          value={filters.paymentMethod}
        >
          <option disabled value="">
            Select Payment Method
          </option>
          <option value="Cash">Cash</option>
          <option value="Credit Card">Credit Card</option>
          <option value="Internet Banking">Internet Banking</option>
          <option value="Paynow">Paynow</option>
          <option value="Paynow QR">Paynow QR</option>
        </select>
      </div>
      <div
        className="d-flex justify-content-between align-items-center mb-4 p-2"
        style={{ gap: "10px" }}
      >
        <select
          className="form-select form-select-sm w-50"
          onChange={(e) => handleFilterChange("month", e.target.value)}
          value={filters.month}
        >
          <option disabled value="">
            Select Type
          </option>
          <option value="monthly">Monthly</option>
          <option value="weekly">Weekly</option>
          <option value="daily">Daily</option>
        </select>

        <CustomDatePicker
          dateFormat="yyyy-MM-dd"
          className="form-control form-control-sm center_list ps-5"
          name="fromDate"
          value={filters.fromDate}
          onChange={(date) =>
            handleFilterChange(
              "fromDate",
              date ? format(date, "yyyy-MM-dd") : ""
            )
          }
          placeholderText="From Date"
        />

        <CustomDatePicker
          dateFormat="yyyy-MM-dd"
          className="form-control form-control-sm center_list ps-5"
          name="toDate"
          value={filters.toDate}
          onChange={(date) =>
            handleFilterChange("toDate", date ? format(date, "yyyy-MM-dd") : "")
          }
          placeholderText="To Date"
        />

        <button
          className="btn border-1 border-secondary shadow-sm btn-sm"
          onClick={handleClearFilters}
        >
          <BsEraser />
        </button>
      </div>

      <div id="chart" className="text-center card p-1">
        <ReactApexChart
          options={chartData.options}
          series={chartData.series}
          type="bar"
          height={350}
        />
      </div>

      <div className="text-center">
        <span
          style={{
            display: "inline-block",
            width: "12px",
            height: "12px",
            backgroundColor: "#0000FF",
            marginRight: "5px",
          }}
        ></span>
        <span>Actual</span>
        <span
          style={{
            display: "inline-block",
            width: "12px",
            height: "12px",
            backgroundColor: "#FF0000",
            marginLeft: "15px",
            marginRight: "5px",
          }}
        ></span>
        <span>Projection</span>
      </div>
    </div>
  );
}

export default RevenueReports;
